import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import round from 'lodash/round';
import moment from 'moment';

import {
  TooltipBulletStyle,
  TooltipItem,
  TooltipItemType,
  TooltipRow,
  TooltipSection,
} from 'common/components/chart-tooltip/types';
import {MATH_TYPE, PRIMATIVE_MATH_TYPES} from 'common/report/constants';
import {XAU_MATH_TYPES} from 'common/report/constants';
import {AbsoluteDateRange} from 'common/report/util/insights/types';
import {
  computeDelta,
  formatDateRanges,
  isIncompleteFirstInterval,
  isIncompleteLastInterval,
} from 'common/report/util/insights/utils';
import {NUMERIC_DATA_PRECISION} from 'common/report/util/insights/utils';
import {commaizeNumber} from 'common/util';
import {isDefinedValue} from 'common/util';
import {formatPercent} from 'common/util';
import {pluralize, truncateMiddle} from 'common/util/string';
import {INSIGHTS_RESOURCE_TYPES} from 'common/widgets/property-filter-menu/models/insights-filter';
import {UNITS} from 'common/util/date';
import {DISPLAY_DATE_FORMATS} from 'common/util/date';
import {formatDate} from 'common/util/date';
import {reporter} from 'common/report/error-reporting';
import {OTHER_SEGMENT_VALUE} from 'common/report/util/insights/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {htmlEncodeString} from 'common/util';

export enum ClauseType {
  Show = `show`,
  Formula = `formula`,
  Other = `other`,
}

type BarChartTooltipStat = {
  count: number;
  dateRange: AbsoluteDateRange;
  percent: number;
};

export type FormulaClauseData = {
  clauseType: ClauseType.Formula;
  metricKey: string;
  value: string;
};

export type ShowClauseData = {
  clauseType: ClauseType.Show;
  metricKey: string;
  math: MATH_TYPE;
  perUserAggregation?: MATH_TYPE;
  value: {name: string};
  // TODO(mack): Convert INSIGHTS_RESOURCE_TYPES to a typescript enum and replace string type with that.
  resourceType: string;
  property?: any;
  dataGroupId?: string;
  // dataGroup is fetched asynchronously, so it could be null even if dataGroupId is set
  dataGroup?: DataGroup;
};

export type OtherClauseData = {
  clauseType: ClauseType.Other;
  // The metric clauses corresponding to the unselected "Other" segments
  metricClauses: MetricClauseData[];
};

export type MetricClauseData = ShowClauseData | FormulaClauseData | OtherClauseData;

type MetricMetadata = {
  metric_key?: string;
  sub_queries?: Array<{metric_key: string}>;
};

type ReportSectionsMetadata = {
  show?: MetricMetadata[];
  formula?: MetricMetadata[];
};

export function computeMetricClauseByKey({
  sectionsMetadata,
  bookmarkParams,
  dataGroups,
}: {
  sectionsMetadata: ReportSectionsMetadata;
  bookmarkParams: any;
  dataGroups: DataGroup[];
}): Record<string, MetricClauseData> {
  const metricClauseByKey: Record<string, MetricClauseData> = {};
  if (sectionsMetadata.show) {
    sectionsMetadata.show.forEach((metadata, index) => {
      const clauseAttrs = bookmarkParams.sections.show[index];

      const dataGroup =
        clauseAttrs.dataGroupId && dataGroups
          ? dataGroups.find((dataGroup) => dataGroup.groupID === clauseAttrs.dataGroupId)
          : null;
      const metricClauseData = {
        ...clauseAttrs,
        ...(dataGroup ? {dataGroup: pick(dataGroup, [`groupID`, `displayName`, `propertyName`])} : {}),
        clauseType: ClauseType.Show,
      };

      if (metadata.sub_queries) {
        metadata.sub_queries
          .filter((subQuery) => subQuery.metric_key)
          .forEach((subQuery) => {
            metricClauseByKey[subQuery.metric_key] = metricClauseData;
          });
      } else if (metadata.metric_key) {
        metricClauseByKey[metadata.metric_key] = metricClauseData;
      }
    });
  }

  if (sectionsMetadata.formula) {
    sectionsMetadata.formula.forEach((metadata, index) => {
      if (metadata.metric_key) {
        metricClauseByKey[metadata.metric_key] = {
          ...bookmarkParams.sections.formula[index],
          clauseType: ClauseType.Formula,
        };
      }
    });
  }

  return mapValues(metricClauseByKey, (metricClauseData, metricKey) => ({
    ...metricClauseData,
    metricKey,
  }));
}

type FormattedMetricUnit =
  | string
  | [
      string, // singular
      string, // plural
    ];

function getMetricTooltipItems(count: number, metricUnit?: FormattedMetricUnit): TooltipItem[] {
  metricUnit = Array.isArray(metricUnit) ? metricUnit : [metricUnit, null];
  return [
    {text: commaizeNumber(count), type: TooltipItemType.Data},
    ...(metricUnit[0] ? [{text: pluralize(metricUnit[0], count, metricUnit[1]), type: TooltipItemType.Text}] : []),
  ];
}

export type ComputeMetricNameRowsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
};

export function computeMetricNameRows({metricClauseData, segments}: ComputeMetricNameRowsParams): TooltipRow[] {
  const hasHistogramSegment =
    metricClauseData.clauseType === ClauseType.Show && metricClauseData.math === PRIMATIVE_MATH_TYPES.HISTOGRAM;
  const breakdownSegments = hasHistogramSegment ? segments.slice(0, -1) : segments;
  const histogramSegment = hasHistogramSegment ? segments[segments.length - 1] : null;

  let metricDisplayName;
  if (metricClauseData.clauseType === ClauseType.Other) {
    const unselectedSegmentCount = metricClauseData.metricClauses.length;
    metricDisplayName = `Other (${unselectedSegmentCount} unselected ${pluralize(`value`, unselectedSegmentCount)})`;
  } else {
    metricDisplayName = metricClauseData.metricKey;
  }
  if (histogramSegment) {
    metricDisplayName = `${metricDisplayName} (${histogramSegment})`;
  }

  const rows: TooltipRow[] = [
    {
      items: [
        {
          text: metricDisplayName,
          type: TooltipItemType.Strong,
        },
      ],
    },
  ];

  if (breakdownSegments && breakdownSegments.length) {
    const segmentText = breakdownSegments
      .map((segment) => (segment === OTHER_SEGMENT_VALUE ? `Other (unselected values)` : segment))
      .join(` / `);
    rows.push({
      items: [
        {
          text: truncateMiddle(segmentText, 45),
          type: TooltipItemType.Text,
        },
      ],
    });
  }

  return rows;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function computeMetricUnit(metricClauseData: MetricClauseData): FormattedMetricUnit {
  if (metricClauseData.clauseType === ClauseType.Other) {
    const metricUnit = computeMetricUnit(metricClauseData.metricClauses[0]);
    // Only show the unit if all the metrics in the "Other" segment are the same unit rather than mixed units
    return metricClauseData.metricClauses.slice(1).every((clause) => isEqual(computeMetricUnit(clause), metricUnit))
      ? metricUnit
      : null;
  } else if (metricClauseData.clauseType !== ClauseType.Show) {
    return null;
  }

  let metricUnit: FormattedMetricUnit = null;
  const dataGroupName = metricClauseData.dataGroupId ? metricClauseData.dataGroup?.displayName : `user`;

  if (metricClauseData.resourceType === INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_COHORTS) {
    metricUnit = dataGroupName;
  } else if (metricClauseData.resourceType === INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_EVENTS) {
    if (metricClauseData.perUserAggregation) {
      if (metricClauseData.math === PRIMATIVE_MATH_TYPES.HISTOGRAM) {
        metricUnit = dataGroupName;
      } else if (metricClauseData.property) {
        metricUnit = dataGroupName ? [`per ${dataGroupName}`, `per ${dataGroupName}`] : null;
      } else if (metricClauseData.perUserAggregation === PRIMATIVE_MATH_TYPES.TOTAL) {
        metricUnit = dataGroupName ? [`event per ${dataGroupName}`, `events per ${dataGroupName}`] : null;
      }
    } else if (!metricClauseData.property) {
      metricUnit = {
        [XAU_MATH_TYPES.DAU]: dataGroupName ? `daily active ${dataGroupName}` : null,
        [XAU_MATH_TYPES.WAU]: dataGroupName ? `weekly active ${dataGroupName}` : null,
        [XAU_MATH_TYPES.MAU]: dataGroupName ? `monthly active ${dataGroupName}` : null,
        [PRIMATIVE_MATH_TYPES.SESSIONS]: `session`,
        [PRIMATIVE_MATH_TYPES.TOTAL]: `event`,
        [PRIMATIVE_MATH_TYPES.UNIQUE]: dataGroupName,
      }[metricClauseData.math];
    }
  } else if (
    metricClauseData.resourceType === INSIGHTS_RESOURCE_TYPES.RESOURCE_TYPE_PEOPLE &&
    !metricClauseData.property &&
    metricClauseData.math === PRIMATIVE_MATH_TYPES.TOTAL
  ) {
    metricUnit = metricClauseData.dataGroupId ? dataGroupName : `profile`;
  }

  return metricUnit;
}

export type ComputeBarChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: BarChartTooltipStat;
  timeComparisonStat?: BarChartTooltipStat;
  titleColor?: string;
};

export function computeBarChartTooltipSections(params: ComputeBarChartTooltipSectionsParams) {
  const {segments, metricClauseData, stat, timeComparisonStat, titleColor} = params;

  const metricUnit = computeMetricUnit(metricClauseData);
  const metricNameRows: TooltipRow[] = computeMetricNameRows({metricClauseData, segments});

  const sections: TooltipSection[] = [{rows: metricNameRows}];

  if (timeComparisonStat) {
    const [formattedDateRange, formattedTimeComparisonDateRange] = formatDateRanges([
      stat.dateRange,
      timeComparisonStat.dateRange,
    ]);

    const delta = timeComparisonStat.count === 0 ? 1 : computeDelta(stat.count, timeComparisonStat.count);
    const formattedDelta = formatPercent(delta);

    const tooltipRows = [
      {
        items: getMetricTooltipItems(stat.count, metricUnit),
      },
      {
        items: [
          {
            text: delta > 0 ? `+${formattedDelta}` : formattedDelta,
            theme: delta < 0 ? `negative` : `positive`,
            type: TooltipItemType.Data,
          },
          {
            text: `from previous period`,
            type: TooltipItemType.Text,
          },
        ],
      },
    ];
    sections.push(
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Solid,
        rows: [
          {
            items: [
              {
                text: formattedDateRange,
                type: TooltipItemType.Detail,
              },
            ],
          },
        ].concat(tooltipRows),
      },
      {rows: []}, // for additional spacing to emphasize separation between sections
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Dash,
        rows: [
          {
            items: [
              {
                text: formattedTimeComparisonDateRange,
                type: TooltipItemType.Detail,
              },
            ],
          },
          {
            items: getMetricTooltipItems(timeComparisonStat.count, metricUnit),
          },
        ],
      },
    );
  } else {
    sections.push({
      rows: [
        {
          items: getMetricTooltipItems(stat.count, metricUnit),
        },
        {
          items: [
            {
              text: formatPercent(stat.percent / 100),
              type: TooltipItemType.Data,
            },
            {
              text: `of total`,
              type: TooltipItemType.Text,
            },
          ],
        },
      ],
    });
  }

  return sections;
}

function timestampToXauDateRangeFunction({unit, operator, isLastInterval, lastIntervalToDate}) {
  const MAU_DAYS = 30;
  const WAU_DAYS = 7;
  // These are the only units allowed in XAU queries, and the only ones this function handles
  const ALLOWED_UNITS = [UNITS[`day`], UNITS[`week`], UNITS[`month`]];
  if (!ALLOWED_UNITS.includes(unit)) {
    reporter.error(new Error(`Unit not in ${ALLOWED_UNITS}: "${unit}"`));
  }

  return function (timestamp) {
    const format = DISPLAY_DATE_FORMATS[`week`];
    const mToDate = moment(lastIntervalToDate);
    const mIntervalStartDate = moment(timestamp);
    // Calculate the end date for the date range:
    // timestamp + 6 days (for week because range is inclusive) or end of month, unless > lastIntervalToDate
    let mEndDate;
    if (unit === UNITS[`month`]) {
      mEndDate = isLastInterval ? mToDate : moment(mIntervalStartDate).endOf(`month`);
    } else if (unit === UNITS[`week`]) {
      mEndDate = isLastInterval ? mToDate : moment(mIntervalStartDate).add(WAU_DAYS - 1, `days`);
    } else if (unit === UNITS[`day`]) {
      mEndDate = mIntervalStartDate;
    }

    let mStartDate;
    if (operator === XAU_MATH_TYPES.MAU) {
      mStartDate = moment(mEndDate).subtract(MAU_DAYS - 1, `days`);
    } else if (operator === XAU_MATH_TYPES.WAU) {
      mStartDate = moment(mEndDate).subtract(WAU_DAYS - 1, `days`);
    }

    return `${mStartDate.format(format)} - ${mEndDate.format(format)}`;
  };
}

export function timestampToTimeUnitFunction({
  displayRangeIfWeek = true,
  unit,
}: {
  displayRangeIfWeek?: boolean;
  unit: string;
}) {
  const customFormatting = {day: `ddd, MMM D`};
  return (timestamp) => formatDate(timestamp, {unit, displayRangeIfWeek, customFormatting});
}

export type LineChartTooltipStat = {
  value: number;
  timestamp?: number;
  dateRange?: AbsoluteDateRange;
};

export type ComputeLineChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: LineChartTooltipStat;
  deltaStat?: LineChartTooltipStat;
  timeComparisonStat?: LineChartTooltipStat;
  timeUnit: string;
  titleColor: string;
  // Used in stacked line chart mode
  percent?: number;
  isLastInterval: boolean;
};

export function computeLineChartTooltipSections(params: ComputeLineChartTooltipSectionsParams) {
  const formatDelta = (delta) => {
    if (!isDefinedValue(delta)) {
      return `N/A`;
    }
    const sign = delta > 0 ? `+` : ``;
    return `${sign}${formatPercent(delta)}`;
  };

  const theme = (delta): string => {
    if (!isDefinedValue(delta) || delta === 0) {
      return ``;
    }
    return delta > 0 ? `positive` : `negative`;
  };

  const {
    segments,
    metricClauseData,
    stat,
    deltaStat,
    timeComparisonStat,
    percent,
    timeUnit,
    isLastInterval,
    titleColor,
  } = params;

  const metricUnit = computeMetricUnit(metricClauseData);
  const metricNameRows: TooltipRow[] = computeMetricNameRows({metricClauseData, segments});

  let timestampFormatter;
  // the operators that require a special date range calculation
  const xauDateRangeOperators: MATH_TYPE[] = [XAU_MATH_TYPES.MAU, XAU_MATH_TYPES.WAU];
  if (metricClauseData.clauseType === ClauseType.Show && xauDateRangeOperators.includes(metricClauseData.math)) {
    timestampFormatter = timestampToXauDateRangeFunction({
      operator: metricClauseData.math,
      unit: timeUnit,
      isLastInterval,
      lastIntervalToDate: stat.dateRange.toDate,
    });
  } else {
    timestampFormatter = timestampToTimeUnitFunction({unit: timeUnit});
  }

  const sections: TooltipSection[] = [];

  if (!timeComparisonStat) {
    sections.push({
      rows: [
        ...metricNameRows,
        {
          items: [
            {
              text: timestampFormatter(stat.timestamp),
              type: TooltipItemType.Detail,
            },
          ],
        },
      ],
    });
  } else {
    sections.push({rows: metricNameRows});
  }

  if (timeComparisonStat) {
    const tooltipRows: TooltipRow[] = [
      {
        items: getMetricTooltipItems(round(stat.value, NUMERIC_DATA_PRECISION), metricUnit),
      },
    ];

    if (Number.isFinite(timeComparisonStat.value)) {
      // May be null when time comparison data is incomplete due to a new project/ data loss/ etc
      const delta = computeDelta(stat.value, timeComparisonStat.value);
      tooltipRows.push({
        items: [
          {
            text: formatDelta(delta),
            type: TooltipItemType.Data,
            theme: theme(delta),
          },
          {
            text: `from previous period`,
            type: TooltipItemType.Text,
          },
        ],
      });
    }

    sections.push(
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Solid,
        rows: [
          {
            items: [
              {
                text: timestampFormatter(stat.timestamp),
                type: TooltipItemType.Detail,
              },
            ],
          },
        ].concat(tooltipRows),
      },
      {rows: []}, // for additional spacing for extra emphasis in this case
      {
        bulletColor: titleColor,
        bulletStyle: TooltipBulletStyle.Dash,
        rows: [
          {
            items: [
              {
                text: timestampFormatter(timeComparisonStat.timestamp),
                type: TooltipItemType.Detail,
              },
            ],
          },
          {
            items: getMetricTooltipItems(round(timeComparisonStat.value, NUMERIC_DATA_PRECISION), metricUnit),
          },
        ],
      },
    );
  } else {
    const tooltipRows: TooltipRow[] = [
      {
        items: getMetricTooltipItems(round(stat.value, NUMERIC_DATA_PRECISION), metricUnit),
      },
    ];

    if (Number.isFinite(percent)) {
      tooltipRows.push({
        items: [
          {
            text: formatPercent(percent / 100),
            type: TooltipItemType.Data,
          },
          {
            text: `of total`,
            type: TooltipItemType.Text,
          },
        ],
      });
    } else if (Number.isFinite(deltaStat?.value)) {
      // May be null when time comparison data is incomplete due to a new project/ data loss/ etc
      const delta = computeDelta(stat.value, deltaStat.value);
      tooltipRows.push({
        items: [
          {
            text: formatDelta(delta),
            type: TooltipItemType.Data,
            theme: theme(delta),
          },
          {
            text: `from previous ${timeUnit}`,
            type: TooltipItemType.Text,
          },
        ],
      });
    }

    sections.push({rows: tooltipRows});
  }

  return sections;
}

export type RenderLineChartTooltipParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: LineChartTooltipStat;
  deltaStat?: LineChartTooltipStat;
  timeComparisonStat?: LineChartTooltipStat;
  timeUnit: string;
  titleColor: string;
  percent?: number;
  isFirstInterval: boolean;
  isLastInterval: boolean;
  utcOffset: number;
};

export function renderLineChartTooltip({
  metricClauseData,
  segments,
  stat,
  deltaStat,
  timeComparisonStat,
  timeUnit,
  titleColor,
  percent,
  isFirstInterval,
  isLastInterval,
  utcOffset,
}: RenderLineChartTooltipParams) {
  const mathOperator = metricClauseData.clauseType === ClauseType.Show ? metricClauseData.math : null;

  const lastIntervalToDate = stat.dateRange.toDate;
  let isIncomplete = false;
  if (isFirstInterval) {
    isIncomplete = isIncompleteFirstInterval(stat.timestamp, stat.dateRange.fromDate, {
      mathOperator,
      utcOffset,
    });
  } else if (isLastInterval) {
    isIncomplete = isIncompleteLastInterval(stat.timestamp, lastIntervalToDate, {
      mathOperator,
      unit: timeUnit,
      utcOffset,
    });
  }

  if (timeComparisonStat) {
    if (isFirstInterval) {
      isIncomplete =
        isIncomplete ||
        isIncompleteFirstInterval(timeComparisonStat.timestamp, timeComparisonStat.dateRange.fromDate, {
          mathOperator,
          utcOffset,
        });
    } else if (isLastInterval) {
      isIncomplete =
        isIncomplete ||
        isIncompleteLastInterval(timeComparisonStat.timestamp, timeComparisonStat.dateRange.toDate, {
          mathOperator,
          unit: timeUnit,
          utcOffset,
        });
    }
  }

  const footerTextAttr = isIncomplete ? `footer-text="Incomplete ${timeUnit}"` : ``;
  const titleColorAttr = !timeComparisonStat ? `title-color="${titleColor}"` : ``;

  const sections = computeLineChartTooltipSections({
    metricClauseData,
    segments,
    stat,
    deltaStat,
    timeComparisonStat,
    timeUnit,
    titleColor,
    percent,
    isLastInterval,
  });

  return `<mp-chart-tooltip ${titleColorAttr} sections='${htmlEncodeString(
    JSON.stringify(sections),
  )}'  ${footerTextAttr}>`;
}

export type PieChartTooltipStat = {
  count: number;
  percent: number;
};

export type ComputePieChartTooltipSectionsParams = {
  metricClauseData: MetricClauseData;
  segments: string[];
  stat: PieChartTooltipStat;
};

export function computePieChartTooltipSections(params: ComputePieChartTooltipSectionsParams) {
  const {metricClauseData, segments, stat} = params;

  return [
    {
      rows: computeMetricNameRows({metricClauseData, segments}),
    },
    {
      rows: [
        {
          items: getMetricTooltipItems(stat.count, computeMetricUnit(metricClauseData)),
        },
        {
          items: [
            {
              text: formatPercent(stat.percent / 100),
              type: TooltipItemType.Data,
            },
            {
              text: `of total`,
              type: TooltipItemType.Text,
            },
          ],
        },
      ],
    },
  ];
}

type RenderPieChartTooltipParams = {
  value: number;
  percent: number;
  color: string;
  metricKey?: string;
  segments?: string[];
  otherMetricKeys?: string[];
  metricClauseByKey: Record<string, MetricClauseData>;
};

export function renderPieChartTooltip({
  value,
  percent,
  color,
  metricKey,
  segments,
  otherMetricKeys,
  metricClauseByKey,
}: RenderPieChartTooltipParams): string {
  let metricClauseData: MetricClauseData;
  if (otherMetricKeys) {
    metricClauseData = {
      clauseType: ClauseType.Other,
      metricClauses: otherMetricKeys.map((metricKey) => metricClauseByKey[metricKey]),
    };
    segments = [];
  } else {
    metricClauseData = metricClauseByKey[metricKey];
  }

  const sections = computePieChartTooltipSections({
    metricClauseData,
    segments,
    stat: {
      count: value,
      percent,
    },
  });

  return `<mp-chart-tooltip title-color="${color}" sections='${htmlEncodeString(JSON.stringify(sections))}'>`;
}
